import { Route } from '@angular/router';
import { Login } from '@features/auth/login/login';
import { Home } from '@features/home/home';
import { Layout } from '@core/layout/layout';
import { Agents } from '@features/agents/agents/agents';
import { AgentCreateComponent } from '@features/agents/agent-create/agent-create';
import { AgentEditComponent } from '@features/agents/agent-edit/agent-edit';
import { AgentTestComponent } from '@features/agents/agent-test/agent-test';
import { SpacesComponent } from '@features/spaces/spaces/spaces';
import { SpaceCreateComponent } from '@features/spaces/space-create/space-create';
import { SpaceEditComponent } from '@features/spaces/space-edit/space-edit';
import { TokenComponent } from '@features/auth/token/token';
import { WebhookToolComponent } from '@features/tools/webhook-tool/webhook-tool';
import { ToolsComponent } from '@features/tools/tools/tools';
import { TestType } from '@generated/models/test-type';
import { TestsComponent } from '@features/tests/tests/tests.component';
import { TestEditComponent } from '@features/tests/test-edit/test-edit.component';
import { ConversationsShellComponent } from '@features/conversations/conversations-shell/conversations-shell.component';
import { SessionsListComponent } from '@features/conversations/sessions-list/sessions-list.component';
import { CallsListComponent } from '@features/conversations/calls-list/calls-list.component';
import { ConversationDetailComponent } from '@features/conversations/conversation-detail/conversation-detail';
import { SettingsComponent } from '@features/settings/settings';
import { SecretEditComponent } from '@features/secrets/secret-edit/secret-edit';
import { PhoneNumbersComponent } from '@features/phone-numbers/phone-numbers/phone-numbers';
import { PhoneNumberEditComponent } from '@features/phone-numbers/phone-number-edit/phone-number-edit';
import { TelecomAccountEditComponent } from '@features/telecom-accounts/telecom-account-edit/telecom-account-edit';
import { OutboundCampaigns } from '@features/campaigns/outbound-campaigns/outbound-campaigns';
import { OutboundCampaignCreate } from '@features/campaigns/outbound-campaign-create/outbound-campaign-create';
import { OutboundCampaignEdit } from '@features/campaigns/outbound-campaign-edit/outbound-campaign-edit';
import { CampaignItems } from '@features/campaigns/campaign-items/campaign-items';
import { AuthGuard } from '@core/guards/auth.guard';
import { UserGuard } from '@core/guards/user.guard';
import { NoAuthGuard } from '@core/guards/no-auth.guard';
import { AdminGuard } from '@core/guards/admin.guard';
import { ProfileComponent } from '@features/profile/profile';
import { AdminSignInComponent } from '@features/auth/admin-sign-in/admin-sign-in';
import { AdminNavigationComponent } from '@features/admin/admin-navigation/admin-navigation';
import { ModelEditComponent } from '@features/admin/model-edit/model-edit';
import { LanguageLocalesComponent } from '@features/admin/language-locales/language-locales';
import { LanguageLocaleEditComponent } from '@features/admin/language-locale-edit/language-locale-edit';
import { PlansComponent } from '@features/admin/plans/plans';
import { AdminSettingsComponent } from '@features/admin/admin-settings/admin-settings';
import { AdminLivekitComponent } from '@features/admin/admin-livekit/admin-livekit';
import { BenchmarksListComponent } from '@features/admin/benchmarks-list/benchmarks-list.component';
import { BenchmarkEditComponent } from '@features/admin/benchmark-edit/benchmark-edit.component';
import { BenchmarkRunsComponent } from '@features/admin/benchmark-runs/benchmark-runs.component';
import { Billing } from '@features/billing/billing';
import { DevelopersComponent } from '@features/developers/developers/developers.component';
import { WebhookEditComponent } from '@features/developers/webhook-edit/webhook-edit.component';
import { OAuthApplicationEditComponent } from '@features/developers/oauth-application-edit/oauth-application-edit.component';
import { WebhookRequestsComponent } from '@features/developers/webhook-requests/webhook-requests.component';
import { InviteActivateComponent } from '@features/auth/invite-activate/invite-activate.component';

/**
 * Marks a route the in-app assistant is allowed to navigate the user to.
 * Read off `Router.config` at runtime (single source of truth — no hardcoded
 * route catalog in the assistant tool). `:param` segments mean the assistant
 * must resolve a real id from page context or a list tool first.
 */
export interface AssistantNav {
  description: string;
}

/** Strongly-typed `Route.data` for this app (best practice: extend Route). */
export interface AppRouteData {
  assistantNav?: AssistantNav;
  /**
   * Помечает роут как full-page маунт AgentTestComponent'а (/agents/:id/test,
   * /published/agents/:id). Компонент читает это в isWidgetMode и рендерит
   * full-layout вне зависимости от input widgetMode (дефолт которого true
   * — под кейс маунтинга через custom-element).
   */
  routeBased?: boolean;
  /**
   * Вид теста для формы создания: /tests/new/simulation и /tests/new/e2e.
   * Тип задаёт рут, а не состояние компонента, — тогда у каждого вида
   * свой адрес, который видит ассистент и которым можно поделиться.
   */
  testType?: TestType;
}

/** `Route` with our typed `data`, so `data.assistantNav` is compiler-checked. */
export interface AppRoute extends Route {
  data?: AppRouteData;
  children?: AppRoute[];
}

export const routes: AppRoute[] = [
  {
    path: 'login',
    component: Login,
    canActivate: [NoAuthGuard]
  },
  {
    path: 'profile',
    component: ProfileComponent,
    canActivate: [AuthGuard],
    data: { assistantNav: { description: 'Your own profile — name, email, password and personal preferences.' } }
  },
  {
    path: 'published/agents/:id',
    component: AgentTestComponent,
    // routeBased=true → AgentTestComponent игнорирует widgetMode input
    // (по-дефолту true под кастом-элемент) и рендерит full-layout: публичная
    // страница /published/agents/:id — обычная landing-страница агента.
    data: { routeBased: true }
  },
  {
    // Публичный просмотр деталей одной сессии. Юзер не залогинен, поэтому
    // авторизация — через ?sessionToken=<jwt> (тот же токен, который бэк
    // вернул на dispatch). ConversationDetailComponent сам читает
    // sessionToken из query-params и прокидывает его через
    // BEARER_TOKEN_OVERRIDE во все AgentsService-запросы. Никакого guard'а —
    // токен и есть авторизация.
    path: 'published/agents/:agentId/conversations/:sessionId',
    component: ConversationDetailComponent,
  },
  {
    path: '',
    component: Layout,
    canActivate: [UserGuard],
    children: [
      {
        path: 'home',
        redirectTo: '/agents',
        pathMatch: 'full'
      },
      {
        path: 'agents',
        component: Agents,
        data: { assistantNav: { description: 'Agents list — every voice agent in the current workspace. Start here to find an agent by name and get its id.' } }
      },
      {
        path: 'agents/new',
        component: AgentCreateComponent,
        data: { assistantNav: { description: 'New agent form — name, language, first message and system prompt for an agent that does not exist yet.' } }
      },
      {
        path: 'agents/:id/duplicate',
        component: AgentCreateComponent,
        data: { assistantNav: { description: 'Duplicate an agent (needs the source agent id) — creates a copy so a risky change can be tried without touching the live agent.' } }
      },
      {
        path: 'agents/:id',
        component: AgentEditComponent,
        data: { assistantNav: { description: 'Agent editor (needs the agent id) — the main page for changing an agent: name, working mode, languages and first messages, system prompt, models, widget publishing. This is where a prompt rewrite happens. It is also the ONLY place where tools are configured FOR THIS AGENT: both built-in ones (end the call, transfer to an operator or to another agent, ask for clarification, detect language, skip a turn, answer from knowledge) and the webhook tools attached to it. Their per-agent settings — own description for the LLM, whether the caller may interrupt, transfer rules — are fields of this page, not of the tools list.' } }
      },
      {
        path: 'agents/:id/test',
        component: AgentTestComponent,
        // routeBased → full-layout как раньше, без embed-режима.
        data: {
          routeBased: true,
          assistantNav: { description: 'Talk to an agent (needs the agent id) — a live browser conversation for trying the agent out by hand.' }
        }
      },
      {
        path: 'agents/:agentId/conversations/:sessionId',
        component: ConversationDetailComponent,
        data: { assistantNav: { description: 'One conversation in full (needs agentId and sessionId) — transcript, timings, recording and what the agent called during the call.' } }
      },
      {
        // Вне conversations-shell'а: у того свои табы «сессии/звонки», и
        // карточка внутри них выглядела бы как ещё один пункт списка.
        path: 'calls/:callId',
        loadComponent: () =>
          import('@features/conversations/call-detail/call-detail.component')
            .then(m => m.CallDetailComponent),
        data: { assistantNav: { description: 'One call card (needs callId) — numbers, provider, timings, end reason, agent variables and the SIP INVITE headers the caller\'s PBX sent.' } }
      },
      {
        path: 'spaces',
        component: SpacesComponent,
        data: { assistantNav: { description: 'Workspaces list — the spaces the user belongs to; everything else (agents, numbers, billing) hangs off the current one.' } }
      },
      {
        path: 'spaces/new',
        component: SpaceCreateComponent,
        data: { assistantNav: { description: 'New workspace form.' } }
      },
      {
        path: 'spaces/:id',
        component: SpaceEditComponent,
        data: { assistantNav: { description: 'Workspace settings (needs the workspace id) — name, members and defaults.' } }
      },
      {
        path: 'tools',
        component: ToolsComponent,
        data: { assistantNav: { description: 'Webhook tools list — HTTP integrations the user created, each with its own URL, headers and body schema. ONLY webhook tools live here. Built-in tools of the platform (end the call, transfer to an operator, hand off to another agent, ask for clarification, detect language, skip a turn, answer from knowledge) are NOT on this page: they exist for every agent and are switched on and configured per agent, in the Tools section of the agent editor.' } }
      },
      {
        path: 'tests',
        component: TestsComponent,
        data: { assistantNav: { description: 'Tests list — evaluation scenarios: a scripted conversation plus success criteria, used to check an agent still behaves after a change.' } }
      },
      {
        path: 'tests/runs',
        component: TestsComponent,
        data: { assistantNav: { description: 'Test runs history — results of past evaluation runs, pass/fail per scenario.' } }
      },
      {
        // Старый адрес: ведёт на симуляцию. Редирект, а не удаление —
        // на него смотрят закладки и кнопка «Создать тест».
        path: 'tests/new',
        redirectTo: 'tests/new/simulation',
        pathMatch: 'full'
      },
      {
        // По руту на тип теста. Ассистент ходит по приложению ТОЛЬКО
        // через руты (assistant.service.collectNavRoutes читает
        // Router.config), и клик по вкладке для него не существует: у
        // вкладки нет адреса. Без этих рутов «создай e2e-тест» было
        // невыполнимо — агент попадал на /tests/new, то есть на симуляцию.
        path: 'tests/new/simulation',
        component: TestEditComponent,
        data: {
          testType: 'Simulation',
          assistantNav: {
            description:
              'New simulation test — a text-only evaluation: a scripted caller talks to the agent ' +
              'as text, no room, no audio, no recording. Write the scenario and the success criteria. ' +
              'Cheap, good for checking prompt changes.',
          },
        }
      },
      {
        path: 'tests/new/e2e',
        component: TestEditComponent,
        data: {
          testType: 'E2E',
          assistantNav: {
            description:
              'New end-to-end test — a real session: a machine calls the agent with its own voice, ' +
              'the run has a LiveKit room, audio, a recording and ordinary call billing. Besides the ' +
              'scenario it needs a caller persona and the models the caller hears and speaks with. ' +
              'This is the only kind that catches speech recognition, synthesis, pauses, ' +
              'interruptions and recording problems.',
          },
        }
      },
      {
        path: 'tests/:id',
        component: TestEditComponent,
        data: { assistantNav: { description: 'Edit an evaluation test (needs the test id) — scenario script and success criteria.' } }
      },
      {
        // Раздел аналитики грузится лениво: дашборд тянет chart.js, и платить
        // за него на входе в агентов незачем.
        path: 'analytics',
        loadComponent: () =>
          import('@features/analytics/analytics-shell/analytics-shell.component')
            .then(m => m.AnalyticsShellComponent),
        children: [
          { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
          {
            path: 'dashboard',
            loadComponent: () =>
              import('@features/analytics/analytics-dashboard/analytics-dashboard.component')
                .then(m => m.AnalyticsDashboardComponent),
            data: { assistantNav: { description: 'Analytics dashboard — aggregated conversation quality over time.' } }
          },
          {
            path: 'conversations',
            loadComponent: () =>
              import('@features/analytics/conversation-list/conversation-list.component')
                .then(m => m.ConversationListComponent),
            data: { assistantNav: { description: 'Analysed calls — every scored conversation with its metric values; use it to see where an agent loses points.' } }
          },
          {
            path: 'conversations/:sessionId',
            loadComponent: () =>
              import('@features/analytics/conversation-card/conversation-card.component')
                .then(m => m.ConversationCardComponent),
            data: { assistantNav: { description: 'Call card (needs sessionId) — transcript, per-metric scores and the recording of one analysed conversation.' } }
          },
          {
            path: 'metrics',
            loadComponent: () =>
              import('@features/analytics/metrics-settings/metrics-settings.component')
                .then(m => m.MetricsSettingsComponent),
            data: { assistantNav: { description: 'Metrics catalogue — the conversation metrics the analytics judge scores against.' } }
          },
          {
            path: 'views',
            loadComponent: () =>
              import('@features/analytics/views-page/views-page.component')
                .then(m => m.ViewsPageComponent),
            data: { assistantNav: { description: 'Saved analytics views and the report builder.' } }
          },
          {
            path: 'settings',
            loadComponent: () =>
              import('@features/analytics/analytics-settings/analytics-settings.component')
                .then(m => m.AnalyticsSettingsComponent),
            data: { assistantNav: { description: 'Analytics settings — which model judges conversations and who counts as staff.' } }
          }
        ]
      },
      {
        path: 'conversations',
        component: ConversationsShellComponent,
        children: [
          { path: '', redirectTo: 'sessions', pathMatch: 'full' },
          {
            path: 'sessions',
            component: SessionsListComponent,
            data: { assistantNav: { description: 'Sessions history — every conversation of every agent in the workspace, newest first, filterable by agent and status.' } },
          },
          {
            path: 'calls',
            component: CallsListComponent,
            data: { assistantNav: { description: 'Calls history — the telephony side of conversations: numbers, direction, duration.' } },
          },
        ],
      },
      {
        path: 'settings',
        component: SettingsComponent,
        data: { assistantNav: { description: 'Account settings — workspace-level preferences and integrations.' } }
      },
      {
        path: 'billing',
        component: Billing,
        data: { assistantNav: { description: 'Billing — plan, invoices and usage.' } }
      },
      {
        path: 'admin',
        component: AdminNavigationComponent,
        canActivate: [AdminGuard],
        children: [
          {
            path: 'models',
            component: ModelEditComponent,
            data: { assistantNav: { description: 'Admin — LLM/TTS/STT model catalogue and gateways (admin only).' } }
          },
          {
            path: 'language-locales',
            component: LanguageLocalesComponent,
            data: { assistantNav: { description: 'Admin — language locales available to agents (admin only).' } }
          },
          {
            path: 'language-locales/new',
            component: LanguageLocaleEditComponent,
            data: { assistantNav: { description: 'Admin — add a language locale (admin only).' } }
          },
          {
            path: 'language-locales/:id/edit',
            component: LanguageLocaleEditComponent,
            data: { assistantNav: { description: 'Admin — edit a language locale (admin only, needs the locale id).' } }
          },
          {
            path: 'plans',
            component: PlansComponent,
            data: { assistantNav: { description: 'Admin — billing plans (admin only).' } }
          },
          {
            path: 'users',
            component: AdminSignInComponent,
            data: { assistantNav: { description: 'Admin — users, and signing in as one of them (admin only).' } }
          },
          {
            path: 'settings',
            component: AdminSettingsComponent,
            data: { assistantNav: { description: 'Admin — platform-wide settings (admin only).' } }
          },
          {
            path: 'livekit',
            component: AdminLivekitComponent,
            data: { assistantNav: { description: 'Admin — LiveKit SIP trunks and dispatch rules (admin only).' } }
          },
          {
            path: 'benchmarks',
            component: BenchmarksListComponent,
            data: { assistantNav: { description: 'Admin — LLM/STT benchmarks (admin only).' } }
          },
          {
            path: 'benchmarks/new',
            component: BenchmarkEditComponent,
            data: { assistantNav: { description: 'Admin — create a benchmark (admin only).' } }
          },
          {
            path: 'benchmarks/:id/edit',
            component: BenchmarkEditComponent,
            data: { assistantNav: { description: 'Admin — edit a benchmark (admin only, needs the benchmark id).' } }
          },
          {
            path: 'benchmarks/:id/runs',
            component: BenchmarkRunsComponent,
            data: { assistantNav: { description: 'Admin — benchmark run history (admin only, needs the benchmark id).' } }
          },
          {
            path: '',
            redirectTo: 'models',
            pathMatch: 'full'
          }
        ]
      },
      {
        path: 'secrets/new',
        component: SecretEditComponent,
        data: { assistantNav: { description: 'New secret — a credential that webhook tools and telecom accounts can reference without exposing its value.' } }
      },
      {
        path: 'phone-numbers',
        component: PhoneNumbersComponent,
        data: { assistantNav: { description: 'Phone numbers list — numbers bought or connected, and which agent answers on each.' } }
      },
      {
        path: 'phone-numbers/:phoneNumberAgentId',
        component: PhoneNumberEditComponent,
        data: { assistantNav: { description: 'Phone number settings (needs the phone-number-agent id) — which agent answers, and inbound/outbound behaviour.' } }
      },
      {
        path: 'telecom-accounts/:id',
        component: TelecomAccountEditComponent,
        data: { assistantNav: { description: 'Telecom account (needs the account id) — SIP/provider credentials behind the numbers: verify, reprovision, edit, delete.' } }
      },
      {
        path: 'outbound-campaigns',
        component: OutboundCampaigns,
        data: { assistantNav: { description: 'Outbound campaigns list — bulk calling campaigns and their progress.' } }
      },
      {
        path: 'outbound-campaigns/new',
        component: OutboundCampaignCreate,
        data: { assistantNav: { description: 'New outbound campaign — pick the agent, the number and the contact list.' } }
      },
      {
        path: 'outbound-campaigns/:id',
        component: OutboundCampaignEdit,
        data: { assistantNav: { description: 'Outbound campaign settings (needs the campaign id).' } }
      },
      {
        path: 'outbound-campaigns/:id/items',
        component: CampaignItems,
        data: { assistantNav: { description: 'Outbound campaign contacts (needs the campaign id) — per-contact call status and results.' } }
      },
      {
        path: 'secrets/:secretId',
        component: SecretEditComponent,
        data: { assistantNav: { description: 'Edit a secret (needs the secret id).' } }
      },
      {
        path: 'token',
        component: TokenComponent
      },
      {
        path: 'webhook-tools/new',
        component: WebhookToolComponent,
        data: { assistantNav: { description: 'New webhook tool — an HTTP call the voice agent can make during a conversation.' } }
      },
      {
        path: 'webhook-tools/:id',
        component: WebhookToolComponent,
        data: { assistantNav: { description: 'Edit a webhook tool (needs the tool id) — URL, auth, request schema and what the agent tells the caller while it runs.' } }
      },
      {
        path: 'developers',
        component: DevelopersComponent,
        data: { assistantNav: { description: 'Developers — platform webhooks that notify your own backend about calls and sessions.' } }
      },
      {
        path: 'developers/oauth-applications',
        component: DevelopersComponent,
        data: { assistantNav: { description: 'Developers — OAuth applications for API access.' } }
      },
      {
        path: 'developers/webhooks/new',
        component: WebhookEditComponent,
        data: { assistantNav: { description: 'New platform webhook — pick the events and the endpoint.' } }
      },
      {
        path: 'developers/webhooks/:id',
        component: WebhookEditComponent,
        data: { assistantNav: { description: 'Edit a platform webhook (needs the webhook id).' } }
      },
      {
        path: 'developers/webhooks/:webhookId/requests',
        component: WebhookRequestsComponent,
        data: { assistantNav: { description: 'Webhook delivery history (needs the webhook id) — what was sent and how the endpoint answered.' } }
      },
      {
        path: 'developers/oauth-applications/new',
        component: OAuthApplicationEditComponent,
        data: { assistantNav: { description: 'New OAuth application.' } }
      },
      {
        path: 'developers/oauth-applications/:id',
        component: OAuthApplicationEditComponent,
        data: { assistantNav: { description: 'Edit an OAuth application (needs the application id).' } }
      },
      {
        path: 'invite/activate',
        component: InviteActivateComponent
      },
      { path: '', redirectTo: '/agents', pathMatch: 'full' }
    ]
  }
];
